/**
 * The native (Capacitor) contract. Inside the WebView there is no import map, so a
 * bare specifier imported through a variable (lazy.ts optionalModule) never resolves:
 * the native entry chunk passes one literal import() per plugin, which Vite splits into
 * lazy chunks, and createProviders refuses to start a native build without them.
 *
 *   const native: NativeLoaders = {
 *     admob: () => import('@capacitor-community/admob'),
 *     store: () => import('@capgo/native-purchases'),
 *     push: () => import('@capacitor/push-notifications'),
 *     share: () => import('@capacitor/share'),
 *     filesystem: () => import('@capacitor/filesystem'),
 *     haptics: () => import('@capacitor/haptics'),
 *     app: () => import('@capacitor/app'),
 *     socialLogin: () => import('@capgo/capacitor-social-login'),
 *     splash: () => import('@capacitor/splash-screen'),
 *     statusBar: () => import('@capacitor/status-bar'),
 *     keyboard: () => import('@capacitor/keyboard'),
 *     review: () => import('@capawesome/capacitor-app-review'),
 *   };
 *
 * Each module type is the subset of the plugin's definitions.d.ts the adapters call,
 * so the game's typecheck fails when an installed plugin drifts from it.
 */
import type { AdMobModule } from '../ads/admob.js';
import type { SocialLoginModule } from '../auth/native.js';
import type { NativeStoreModule } from '../iap/native.js';
import type { Loader } from '../lazy.js';
import type { Platform } from '../platform/detect.js';
import type { CapacitorHapticsModule } from '../platform/haptics.js';
import type { CapacitorAppModule } from '../platform/lifecycle.js';
import type { PushNotificationsModule } from '../push/native.js';
import type { AppReviewModule } from '../review/native.js';
import type { CapacitorFilesystemModule, CapacitorShareModule } from '../share/share.js';
import type { KeyboardModule, SplashScreenModule, StatusBarModule } from './shell.js';

export * from './links.js';
export * from './shell.js';

export interface NativeLoaders {
  admob: Loader<AdMobModule>;
  store: Loader<NativeStoreModule>;
  push: Loader<PushNotificationsModule>;
  share: Loader<CapacitorShareModule>;
  filesystem: Loader<CapacitorFilesystemModule>;
  haptics: Loader<CapacitorHapticsModule>;
  app: Loader<CapacitorAppModule>;
  socialLogin: Loader<SocialLoginModule>;
  splash?: Loader<SplashScreenModule>;
  statusBar?: Loader<StatusBarModule>;
  keyboard?: Loader<KeyboardModule>;
  /** optional like the shell's: a build without it asks for no reviews */
  review?: Loader<AppReviewModule>;
}

/** The package behind each loader. */
export const NATIVE_MODULES: Record<keyof NativeLoaders, string> = {
  admob: '@capacitor-community/admob',
  store: '@capgo/native-purchases',
  push: '@capacitor/push-notifications',
  share: '@capacitor/share',
  filesystem: '@capacitor/filesystem',
  haptics: '@capacitor/haptics',
  app: '@capacitor/app',
  socialLogin: '@capgo/capacitor-social-login',
  splash: '@capacitor/splash-screen',
  statusBar: '@capacitor/status-bar',
  keyboard: '@capacitor/keyboard',
  review: '@capawesome/capacitor-app-review',
};

const REQUIRED = ['admob', 'store', 'push', 'share', 'filesystem', 'haptics', 'app', 'socialLogin'] as const satisfies readonly (keyof NativeLoaders)[];

/** The loaders on a native platform (throws, naming what is missing, when any required one is absent); null elsewhere. */
export function requireNativeLoaders(platform: Platform, loaders: Partial<NativeLoaders> | undefined): NativeLoaders | null {
  if (!platform.native) return null;
  const missing = REQUIRED.filter((k) => typeof loaders?.[k] !== 'function');
  if (missing.length) {
    throw new Error(
      `createProviders: ${platform.kind} needs literal import() loaders in ProvidersConfig.native for ${missing.map((k) => `${k} (${NATIVE_MODULES[k]})`).join(', ')}; ` +
        'a bare specifier does not resolve inside the Capacitor WebView (see NativeLoaders in @mp/adapters)',
    );
  }
  return loaders as NativeLoaders;
}
